import { useEffect, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type { GalleryItemDto } from '@crm/shared';
import { assetUrl } from '../../lib/assetUrl';

type Props = {
  items: GalleryItemDto[];
  initialIndex: number;
  onClose: () => void;
};

/**
 * Lightbox dla galerii (panel + portal publiczny). Overlay z pojedynczym
 * dużym obrazem + strzałki ←→ + Escape do zamknięcia. Pętli się
 * (Z ostatniego → na pierwsze), bo galeria jest mała (max 20).
 */
export function GalleryLightbox({ items, initialIndex, onClose }: Props): ReactNode {
  const { t } = useTranslation();
  const [index, setIndex] = useState(initialIndex);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      else if (e.key === 'ArrowLeft') setIndex((i) => (i - 1 + items.length) % items.length);
      else if (e.key === 'ArrowRight') setIndex((i) => (i + 1) % items.length);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [items.length, onClose]);

  if (items.length === 0) return null;
  const current = items[index]!;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/95 p-2 sm:p-4" onClick={onClose}>
      <button
        type="button"
        onClick={onClose}
        className="absolute right-3 top-3 flex h-12 w-12 items-center justify-center rounded-full bg-white/15 text-3xl text-white shadow-lg ring-1 ring-white/20 hover:bg-white/25"
        aria-label={t('common.cancel')}
      >
        ×
      </button>

      {items.length > 1 && (
        <>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              setIndex((i) => (i - 1 + items.length) % items.length);
            }}
            className="absolute left-3 top-1/2 flex h-14 w-14 -translate-y-1/2 items-center justify-center rounded-full bg-white/15 text-4xl text-white shadow-lg ring-1 ring-white/20 hover:bg-white/25"
            aria-label="prev"
          >
            ‹
          </button>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              setIndex((i) => (i + 1) % items.length);
            }}
            className="absolute right-3 top-1/2 flex h-14 w-14 -translate-y-1/2 items-center justify-center rounded-full bg-white/15 text-4xl text-white shadow-lg ring-1 ring-white/20 hover:bg-white/25"
            aria-label="next"
          >
            ›
          </button>
        </>
      )}

      <div className="flex max-h-full w-full max-w-7xl flex-col items-center gap-4" onClick={(e) => e.stopPropagation()}>
        <img
          src={assetUrl(current.imageUrl)}
          alt={current.caption ?? ''}
          className="max-h-[90vh] w-auto max-w-full rounded-lg object-contain shadow-2xl"
        />
        <div className="text-center text-sm text-white/90">
          {current.caption && <div className="font-medium">{current.caption}</div>}
          {current.service && (
            <div className="mt-1 text-xs text-white/70">{current.service.name}</div>
          )}
          {items.length > 1 && (
            <div className="mt-2 text-xs text-white/60">
              {index + 1} / {items.length}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
